import type { TableColumn } from '@ipfabric/utils';
import { createPathlookupGraphLink } from '@ipfabric/utils';
import { aimFlowTupleToPathLookupParameters } from '@ipfabric/utils/aim/aimFlowPathLookupParameters';
import { distinct } from 'utils/array/distinct';

/** Fields `aimFlowTupleToPathLookupParameters` reads off the row. */
const FLOW_TUPLE_FIELDS = [
  'srcIp',
  'dstIp',
  'protocol',
  'srcPort',
  'dstPort',
] as const;

/**
 * Turn an `applicationFlow` label into a link to the path-lookup preview,
 * pre-filled from the flow's 5-tuple. Replaces the *Open path-lookup* icon the
 * Flows tab used to carry in its Actions column.
 *
 * Only `format.createLinkProps` is replaced, so the cell keeps the normal text
 * render (truncation, tooltip, severity contrast). This cannot be declared in the
 * OAS: its `pathLookup` link type hardcodes `protocol: 'tcp'` and ports 80/10000,
 * which would throw the flow's real 5-tuple away.
 *
 * A flow missing a source/destination IP or protocol yields no link and the cell
 * stays plain text — the same degradation the disabled icon button gave.
 */
export const withPathLookupLink = (
  column: TableColumn<any>,
): TableColumn<any> => {
  // `applicationFlow` is a text column in both tables' OAS. Guarding rather than
  // asserting keeps a type change in the yaml from breaking route creation — the
  // cell simply loses its link.
  if (column.format.type !== 'text') {
    return column;
  }

  return {
    ...column,
    webDependencies: distinct([
      ...column.webDependencies,
      ...FLOW_TUPLE_FIELDS,
    ]),
    format: {
      ...column.format,
      createLinkProps: (row: any) => {
        const parameters = aimFlowTupleToPathLookupParameters(row);

        return parameters
          ? createPathlookupGraphLink({ parameters })
          : undefined;
      },
    },
  };
};
